import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

const DeleteModal = ({
  open,
  title,
  message,
  handleClose,
  handleSuccess,
  handleError,
  deleteAction,
  data,
}) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const operationRef = useRef(0);

  useEffect(() => {
    if (!open) {
      // Invalidate callbacks from a request that outlived the dialog.
      operationRef.current += 1;
      setProcessing(false);
      setError("");
    }
  }, [open]);

  const handleDelete = async () => {
    if (processing) return;

    const operationId = ++operationRef.current;
    setProcessing(true);
    setError("");
    try {
      const res = await deleteAction(data);
      if (operationId !== operationRef.current) return;

      if (res?.data?.status) {
        handleSuccess(res);
      } else {
        const message = res?.data?.message || "The item could not be deleted.";
        setError(message);
        handleError?.(message);
      }
    } catch (error) {
      if (operationId !== operationRef.current) return;

      log.error("Delete request failed:", error);
      const message =
        error?.response?.data?.message || "The item could not be deleted.";
      setError(message);
      handleError?.(message);
    } finally {
      if (operationId === operationRef.current) {
        setProcessing(false);
      }
    }
  };

  const handleDialogClose = () => {
    if (!processing) {
      handleClose?.();
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} className="delete-modal">
      <DialogTitle className="delete-modal-title">
        {title}
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          disabled={processing}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {!processing && <Button onClick={handleDialogClose}>No</Button>}
        <Button onClick={handleDelete} disabled={processing} autoFocus>
          Yes{" "}
          {processing && (
            <CircularProgress
              style={{
                width: "15px",
                height: "15px",
                marginLeft: "5px",
                marginTop: "-2px",
              }}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteModal;
